// Every image art slot (art-assets §3–§6): id, path under assets/, expected pixel size, alpha
// rule, tint rule, the world size factor k, and how its placeholder is produced. The game always
// runs without art (architecture §1.5): a slot with no file uses its placeholder.
//
// Implementer: client render/assets. Consumers: assets/loader.ts, render (k factors),
// scripts/export-placeholders.ts and check-assets (M2).
//
// Two generators per slot:
// - `placeholder`: the runtime bitmap `AssetLoader.image()` falls back to. It is null where
//   something else draws the placeholder live: the renderer for world slots (in the active
//   classic or sketch style), the DOM for the wordmark and favicon (ui/brand.ts), CSS for the
//   card and button frames, and nothing at all for the social preview (its tags are left out);
// - `template`: the size-correct art template `npm run export:placeholders` writes (every slot
//   has one; defaults to `placeholder`).
// Skins are not slots: preset skins come from the manifest, and the twelve placeholder skins are
// in assets/placeholders/skins.ts.

import type { Canvas2D } from '../render/canvas.js';
import {
  drawFavicon,
  drawSocialTemplate,
  drawTouchIcon,
  drawWordmark,
} from './placeholders/wordmark.js';
import {
  drawCellOverlayTemplate,
  drawEjectedTemplate,
  drawPaperTemplate,
  drawPelletTemplate,
  drawSpawnerTemplate,
  drawVirusFedTemplate,
  drawVirusTemplate,
} from './placeholders/world.js';
import {
  drawArrow,
  drawBrWin,
  drawButtonFrameTemplate,
  drawDeathArt,
  drawFrameTemplate,
  drawHowtoMove,
  drawHowtoShoot,
  drawHowtoSplit,
  drawJoystick,
  drawJoystickKnob,
  drawLaggy,
  drawMenuBackdrop,
  drawModeBattleRoyale,
  drawModeExperimental,
  drawModeFfa,
  drawModeTeams,
  drawParty,
  drawRankBadge,
  drawSpinner,
  drawSpinnerStrip,
  drawStickerSoon,
} from './placeholders/ui-art.js';
import { drawIcon, ICON_NAMES } from './placeholders/ui-icons.js';

export type SlotGroup = 'brand' | 'world' | 'ui' | 'icon';

/** Draws a placeholder bitmap of the slot's size (assets/placeholders/*). */
export type PlaceholderFactory = (
  width: number,
  height: number,
) => HTMLCanvasElement | OffscreenCanvas;

export interface ImageSlot {
  /** Stable id, e.g. `world/virus` (the path without the extension). */
  readonly id: string;
  /** Path under assets/ (PNG preferred; a `.webp` sibling is also accepted). */
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly group: SlotGroup;
  /** Must be opaque (paper tiles, touch icon, social, backdrop). */
  readonly opaque: boolean;
  /** Multiply-tinted with an entity colour (pellets, ejected, party arrow). */
  readonly tint: boolean;
  /** World slots: image side = k × collision radius (art-assets §5). */
  readonly k?: number;
  /**
   * Placeholder bitmap, or null when the renderer draws the placeholder procedurally every frame
   * (most world slots: classic circles, viruses, outlines).
   */
  readonly placeholder: PlaceholderFactory | null;
  /** The art template for `export:placeholders` (defaults to `placeholder`). */
  readonly template?: PlaceholderFactory;
}

/** Number of optional pellet variants `world/pellets/pellet-01.png` … `pellet-16.png`. */
export const PELLET_VARIANTS = 16;

/** Turns a generator that may return null (no canvas available) into a PlaceholderFactory. */
function factory(draw: (w: number, h: number) => Canvas2D | null): PlaceholderFactory {
  return (w, h) => {
    const c = draw(w, h);
    if (!c) throw new Error('ball.io: no canvas available for a placeholder');
    return c;
  };
}

interface SlotSpec {
  readonly width: number;
  readonly height?: number;
  readonly opaque?: boolean;
  readonly tint?: boolean;
  readonly k?: number;
  /** Runtime placeholder (null = drawn live elsewhere). */
  readonly placeholder?: ((w: number, h: number) => Canvas2D | null) | null;
  /** Template when it differs from the placeholder. */
  readonly template?: (w: number, h: number) => Canvas2D | null;
}

function slot(id: string, group: SlotGroup, spec: SlotSpec): ImageSlot {
  const placeholder = spec.placeholder ? factory(spec.placeholder) : null;
  const template = spec.template ? factory(spec.template) : (placeholder ?? undefined);
  const s: { -readonly [K in keyof ImageSlot]: ImageSlot[K] } = {
    id,
    path: `${id}.png`,
    width: spec.width,
    height: spec.height ?? spec.width,
    group,
    opaque: spec.opaque ?? false,
    tint: spec.tint ?? false,
    placeholder,
  };
  if (spec.k !== undefined) s.k = spec.k;
  if (template) s.template = template;
  return Object.freeze(s);
}

const brand = (id: string, spec: SlotSpec): ImageSlot => slot(`brand/${id}`, 'brand', spec);
const world = (id: string, spec: SlotSpec): ImageSlot => slot(`world/${id}`, 'world', spec);
const ui = (id: string, spec: SlotSpec): ImageSlot => slot(`ui/${id}`, 'ui', spec);

/** Every image slot (art-assets §3, §5, §6.1, §6.2), in table order. */
export const IMAGE_SLOTS: readonly ImageSlot[] = Object.freeze([
  // §3 Brand. The live wordmark and favicon placeholders are DOM-drawn (ui/brand.ts).
  brand('wordmark', { width: 1200, height: 400, placeholder: null, template: drawWordmark }),
  brand('favicon', { width: 512, placeholder: null, template: drawFavicon }),
  brand('apple-touch-icon', { width: 180, opaque: true, placeholder: drawTouchIcon }),
  brand('social', {
    width: 1200,
    height: 630,
    opaque: true,
    placeholder: null,
    template: drawSocialTemplate,
  }),

  // §5 World. Drawn procedurally in the active style; templates for the artist.
  world('virus', { width: 512, k: 2.3, template: drawVirusTemplate }),
  world('virus-fed', { width: 512, k: 2.3, template: drawVirusFedTemplate }),
  world('spawner', { width: 512, k: 2.3, template: drawSpawnerTemplate }),
  ...Array.from({ length: PELLET_VARIANTS }, (_, i) => {
    const n = String(i + 1).padStart(2, '0');
    return world(`pellets/pellet-${n}`, {
      width: 64,
      k: 2.5,
      tint: true,
      template: (w, h) => drawPelletTemplate(w, h, i + 1),
    });
  }),
  world('ejected', { width: 128, k: 2.4, tint: true, template: drawEjectedTemplate }),
  world('cell-overlay', { width: 512, k: 2.0, template: drawCellOverlayTemplate }),
  // Paper tiles are not entities: one 512 px tile covers 512 × 512 u of world space (no k).
  world('paper-light', {
    width: 512,
    opaque: true,
    template: (w, h) => drawPaperTemplate(w, h, false),
  }),
  world('paper-dark', {
    width: 512,
    opaque: true,
    template: (w, h) => drawPaperTemplate(w, h, true),
  }),

  // §6.1 UI illustrations and frames.
  ui('modes/ffa', { width: 256, placeholder: drawModeFfa }),
  ui('modes/battleroyale', { width: 256, placeholder: drawModeBattleRoyale }),
  ui('modes/teams', { width: 256, placeholder: drawModeTeams }),
  ui('modes/experimental', { width: 256, placeholder: drawModeExperimental }),
  ui('party', { width: 256, placeholder: drawParty }),
  ui('howto-move', { width: 240, placeholder: drawHowtoMove }),
  ui('howto-split', { width: 240, placeholder: drawHowtoSplit }),
  ui('howto-shoot', { width: 240, placeholder: drawHowtoShoot }),
  ui('frame', { width: 96, placeholder: null, template: drawFrameTemplate }),
  ui('button-frame', {
    width: 120,
    height: 60,
    placeholder: null,
    template: drawButtonFrameTemplate,
  }),
  ui('spinner', { width: 256, placeholder: drawSpinner }),
  ui('spinner-strip', { width: 2048, height: 256, placeholder: null, template: drawSpinnerStrip }),
  ui('sticker-soon', { width: 128, height: 64, placeholder: drawStickerSoon }),
  ui('death', { width: 600, height: 400, placeholder: drawDeathArt }),
  ui('br-win', { width: 800, height: 400, placeholder: drawBrWin }),
  ui('rank-1', { width: 256, placeholder: (w, h) => drawRankBadge(1, w, h) }),
  ui('rank-2', { width: 256, placeholder: (w, h) => drawRankBadge(2, w, h) }),
  ui('rank-3', { width: 256, placeholder: (w, h) => drawRankBadge(3, w, h) }),
  ui('arrow', { width: 64, tint: true, placeholder: drawArrow }),
  ui('laggy', { width: 64, placeholder: drawLaggy }),
  ui('joystick', { width: 240, placeholder: drawJoystick }),
  ui('joystick-knob', { width: 112, placeholder: drawJoystickKnob }),
  ui('menu-backdrop', { width: 1920, height: 1080, opaque: true, placeholder: drawMenuBackdrop }),

  // §6.2 Icons: 128 × 128 black shapes on transparent, used as CSS masks.
  ...ICON_NAMES.map((name) =>
    slot(`ui/icons/${name}`, 'icon', { width: 128, placeholder: (w, h) => drawIcon(name, w, h) }),
  ),
]);

const BY_ID = new Map(IMAGE_SLOTS.map((s) => [s.id, s]));
const BY_PATH = new Map(IMAGE_SLOTS.map((s) => [s.path, s]));

export function slotById(id: string): ImageSlot | undefined {
  return BY_ID.get(id);
}

/** The slot a file path belongs to (`.webp` siblings map to the `.png` slot). */
export function slotByPath(path: string): ImageSlot | undefined {
  return BY_PATH.get(path.endsWith('.webp') ? `${path.slice(0, -5)}.png` : path);
}

export function slotsInGroup(group: SlotGroup): ImageSlot[] {
  return IMAGE_SLOTS.filter((s) => s.group === group);
}

/** The generator `export:placeholders` uses for a slot. */
export function slotTemplate(s: ImageSlot): PlaceholderFactory | null {
  return s.template ?? s.placeholder;
}
